import { exportMixInNewLayer, ParentMixerLayer, ValueMixerLayer } from '@api/fileManagerApi';
import CustomTooltip from '@gui/components/generic/CustomTooltip';
import SidePanelDialog from '@gui/components/panels/settings/SidePaneDialog';
import { Panels } from '@gui/constants/Panels';
import { ControllerManager } from '@gui/controller/ControllerManager';
import { Box, Button, CircularProgress, IconButton, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { PlusCircle } from '@untitledui-pro/icons/line';
import { DataState, MixerElement, NotificationType } from '@visualizer/types';
import React, { useEffect, useRef, useState } from 'react';
import { InternalMixerElement } from 'Simulations/LayerMixed';
import { SensorData } from 'Simulations/SensorPoint';
import Grid from '../../../../Geodata/grid';
import { getGrid } from '../../../../store/LoadingStore';
import { getSentence } from '../../../constants/sentences';
import useNotificationStore from '../../../store/notification';
import usePersistentStore, { getLayersState, useGlobalBucket } from '../../../store/persistent/persistentStore';
import LayerTree from './LayerTree';
import MixerRow from './MixerRow';
import { MixerActions } from './MixerTypes';
import MultiMode from './MultiMode';
import { ScalarModeIcon, VectorialModeIcon } from './vectorialScalarModeIcons';
import { useCapabilities } from '../../../../demo/capabilities';
import { getDisabledInEnvMessage, useEnvStore } from 'store/EnvStore';

type MixerProps = {};
const EMPTY_MIXER_ELEMENTS: MixerElement[] = [];

// let mixerElementsCache: MixerElement[] = []; // survives HMR as long as the module isn't fully disposed
const Mixer: React.FC<MixerProps> = ({}) => {
    const { canManageState } = useCapabilities();
    const env = useEnvStore((s) => s.env);
    // Zustand State Variables:
    const data = useGlobalBucket((s) => s.data);
    const { isMixerMode, mixerAndLogic, mixerHigherLogic, multimode } = data;
    const rawMixerElements = usePersistentStore((s) => s.layers?.layerStates?.mixed?.mixerElements);
    const mixerElements = Array.isArray(rawMixerElements) ? rawMixerElements : EMPTY_MIXER_ELEMENTS;

    const [maxLayers] = useState(10);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const showNotification = useNotificationStore((s) => s.showNotification);
    const setElementPanel = usePersistentStore((s) => s.setElementPanel);
    const setActiveLayerId = usePersistentStore((s) => s.setCurrentLayerId);
    const setDataPanel = (updates: Partial<DataState>) => usePersistentStore.getState().setPanel(updates, Panels.DATA);

    const mixedVectorialScalarMode = usePersistentStore((s) => s.layers?.layerStates?.mixed?.VECTORIAL_SCALAR_MODE ?? 0);
    const updateLayerState = usePersistentStore((s) => s.updateLayerState);

    const grid = getGrid();

    const prevActiveRef = useRef<string | null>(null);
    const prevMultimodeRef = useRef<boolean>(multimode);

    const getMixerController = () => ControllerManager.getInstance().getMixerController();

    // Add the mixer layer and remove it when the component mount/unmount
    useEffect(() => {
        if (!isMixerMode) {
            if (!isMixerMode) {
                prevActiveRef.current = null;
            }
            return;
        }

        const store = usePersistentStore.getState();
        // Capture the active layer before we switch to the mixed one
        prevActiveRef.current = store.layers.activeLayerID;

        const mixerController = getMixerController();
        const mixedLayer = mixerController.getMixerLayer();
        mixedLayer.refreshInternalState();
        // Register (only once) and compute values
        const collection = mixedLayer.parentSimulationSet.layerCollection;
        if (!collection.includes(mixedLayer)) {
            collection.push(mixedLayer);
        }
        setActiveLayerId(mixedLayer.getID()); // Set the active layer to the mixed layer
        const currentTransparency = usePersistentStore.getState().getActiveLayerState()?.element?.TRANSPARENCY ?? 0;
        if (!multimode) {
            setElementPanel({ TRANSPARENCY: 0 });
        } else if (currentTransparency === 0) {
            setElementPanel({ TRANSPARENCY: 0.5 });
        }
        return () => {
            const storeNow = usePersistentStore.getState();
            if (!storeNow.global.data.isMixerMode) {
                const previousId = prevActiveRef.current;
                const collection = grid.getLayerCollection();
                const fallbackId = previousId ?? collection.find((layer) => layer !== mixedLayer)?.getID() ?? null;

                const isMixedActive = storeNow.layers.activeLayerID === mixedLayer.getID();
                if (isMixedActive && fallbackId) {
                    // Update grid immediately to prevent render loop from referencing a removed layer
                    grid.setActiveLayerByUID(fallbackId);
                    storeNow.setCurrentLayerId(fallbackId);
                }

                // Remove the exact instance we added if it's still present
                const idx = collection.indexOf(mixedLayer);
                if (idx >= 0) collection.splice(idx, 1);

                // If, for any reason, the active layer is still mixed, force the fallback again
                if (fallbackId && getLayersState().activeLayerID === mixedLayer.getID()) {
                    usePersistentStore.getState().setCurrentLayerId(fallbackId);
                }
            }
        };
    }, [isMixerMode]);

    useEffect(() => {
        if (!isMixerMode) {
            prevMultimodeRef.current = multimode;
            return;
        }

        const wasMultimode = prevMultimodeRef.current;
        if (wasMultimode && !multimode) {
            setElementPanel({ TRANSPARENCY: 0 });
        }
        prevMultimodeRef.current = multimode;
    }, [isMixerMode, multimode, setElementPanel]);

    useEffect(() => {
        if (!isMixerMode) return;
        // mixerElementsCache = mixerElements;
        getMixerController().run();
    }, [isMixerMode, mixerElements]);

    const openLayerDialog = () => setDialogOpen(true);
    const handleCloseLayerDialog = () => setDialogOpen(false);

    const addLayerToMixer = (layerId: string) => {
        if (mixerElements.length >= maxLayers) {
            showNotification(getSentence('Mixer.AddLayer.Button.TooManyLayers'), NotificationType.Warning);
            return;
        }
        getMixerController().addLayer(layerId);
    };

    const actions = (action: MixerActions, layerId?: string, value?: any) => {
        switch (action) {
            case MixerActions.DELETE:
                getMixerController().deleteLayer(layerId as string);
                break;
            case MixerActions.DELETE_ALL:
                getMixerController().deleteAllLayers();
                break;
            case MixerActions.TOGGLE_LOCK:
                getMixerController().toggleLock(layerId as string);
                break;
            case MixerActions.TOGGLE_VISIBLE:
                getMixerController().toggleVisible(layerId as string);
                break;
            case MixerActions.CHANGE_WEIGHT:
                getMixerController().changeWeight(layerId as string, value);
                break;
            case MixerActions.RESET_WEIGHT:
                getMixerController().resetWeight();
                break;
            case MixerActions.TOGGLE_INVERT:
                getMixerController().toggleInvert(layerId as string);
                break;
            case MixerActions.IS_0_1_NORMALIZATION:
                getMixerController().isZeroOneNormalization(layerId as string);
                break;
            default:
                showNotification(`Unknown action: ${action}`, NotificationType.Error);
                break;
        }
    };

    const convertFrame = (sensors: SensorData[]): ValueMixerLayer[] => {
        return sensors.map((s) => {
            return {
                id: s.getID(),
                value: s.getScalarValue(),
                direction: s.getVectorialResult(),
            };
        });
    };

    const convertParents = (parents: Map<string, InternalMixerElement>): ParentMixerLayer[] => {
        let parentMixerLayers: ParentMixerLayer[] = [];
        parents.forEach((values, id) => {
            parentMixerLayers.push({
                layer: id,
                weight: values.weight,
                invert: values.invert,
                isRangeNormalization: values.isRangeNormalization,
            });
        });
        return parentMixerLayers;
    };

    const exportMix = async () => {
        if (!grid) return;

        const mixedLayer = getMixerController().getMixerLayer();

        try {
            setSaving(true);
            const layerName = `Mixed Layer-${Date.now()}`;
            const response = await exportMixInNewLayer(
                Grid.sensorPointsN,
                convertParents(mixedLayer.parents),
                convertFrame(mixedLayer.runPipeline({ suppressNotifications: true, octTree: getGrid().octTree! }).data),
                layerName
            );

            await grid.getSimulationSet().loadSavedLayer(response);
            useNotificationStore.getState().showNotification('Layer saved', NotificationType.Success);
        } catch (e) {
            console.error(e);
            useNotificationStore.getState().showNotification('Error saving frame in new layer', NotificationType.Error);
        } finally {
            setSaving(false);
        }
    };

    const handleToggleMultiMode = () => {
        if (multimode) {
            const store = usePersistentStore.getState();
            const mixedMode = store.layers.layerStates.mixed?.VECTORIAL_SCALAR_MODE ?? 0;
            const mismatchedLayerNames: string[] = [];

            for (const el of mixerElements) {
                const layerMode = store.layers.layerStates[el.layerId]?.VECTORIAL_SCALAR_MODE ?? 0;
                if (layerMode !== mixedMode) {
                    const layerName = getGrid().searchLayerByUID(el.layerId)?.name ?? el.layerId;
                    mismatchedLayerNames.push(layerName);
                }
            }

            if (mismatchedLayerNames.length > 0) {
                showNotification(
                    `Mixed mode accepts only layers with the same Value type (Vectorial or Scalar), please uniform and try again. Mismatched layers: ${mismatchedLayerNames.join(', ')}.`,
                    NotificationType.Error
                );
                return;
            }
        }

        setDataPanel({ multimode: !multimode });
    };

    return (
        <>
            <Box className="mixer-action-bar">
                <Button size="small" variant={multimode ? 'contained' : 'outlined'} onClick={handleToggleMultiMode}>
                    Multi
                </Button>

                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={mixerAndLogic ? 'and' : 'or'}
                    onChange={(_, next) => {
                        if (next == null) return;
                        getMixerController().toggleAndORLogic(next);
                    }}
                    className="panel-toggle-group"
                >
                    <ToggleButton value="and">And</ToggleButton>
                    <ToggleButton value="or">Or</ToggleButton>
                </ToggleButtonGroup>

                {multimode && (
                    <Button
                        size="small"
                        variant={mixerHigherLogic ? 'contained' : 'outlined'}
                        onClick={() => getMixerController().toggleHigherLogic(!mixerHigherLogic)}
                    >
                        Highest
                    </Button>
                )}

                <Box className={`mixer-action-bar__secondary${multimode ? ' is-hidden' : ''}`}>
                    <CustomTooltip side="left" title={getSentence('Mixer.Multi.Reset.Tooltip')}>
                        <Button variant="contained" onClick={() => actions(MixerActions.RESET_WEIGHT)}>
                            Reset
                        </Button>
                    </CustomTooltip>
                    <Tooltip title={!canManageState ? getDisabledInEnvMessage(env) : ''}>
                        <span>
                            <Button variant="contained" disabled={saving || !canManageState} onClick={exportMix}>
                                Save
                                {saving && <CircularProgress size={16} color="inherit" sx={{ ml: 1 }} />}
                            </Button>
                        </span>
                    </Tooltip>
                </Box>

                <Box className={`mixer-action-bar__mode-toggle${multimode ? ' is-hidden' : ''}`}>
                    <CustomTooltip side="left" title={mixedVectorialScalarMode === 0 ? 'Vectorial mode' : 'Scalar mode'}>
                        <span>
                            <IconButton
                                className="mixer-action-icon-button"
                                size="small"
                                disabled={mixerElements.length > 0}
                                onClick={() => updateLayerState('mixed', { VECTORIAL_SCALAR_MODE: mixedVectorialScalarMode === 0 ? 1 : 0 })}
                                aria-label={mixedVectorialScalarMode === 0 ? 'Vectorial mode' : 'Scalar mode'}
                            >
                                {mixedVectorialScalarMode === 0 ? (
                                    <VectorialModeIcon className="mixer-mode-toggle__icon" />
                                ) : (
                                    <ScalarModeIcon className="mixer-mode-toggle__icon" />
                                )}
                            </IconButton>
                        </span>
                    </CustomTooltip>
                </Box>

                <Box className={`mixer-action-bar__add${saving ? ' is-hidden' : ''}`}>
                    <CustomTooltip
                        side="left"
                        title={
                            mixerElements.length >= maxLayers
                                ? getSentence('Mixer.AddLayer.Button.TooManyLayers')
                                : getSentence('Mixer.AddLayer.Button.Tooltip')
                        }
                    >
                        <span>
                            <IconButton
                                className="mixer-action-icon-button"
                                onClick={openLayerDialog}
                                disabled={mixerElements.length >= maxLayers}
                                data-testid="mixer-add-layer"
                            >
                                <PlusCircle className="icon-size-medium" />
                            </IconButton>
                        </span>
                    </CustomTooltip>
                </Box>
            </Box>
            <Box className={`mixer-action-row-secondary${multimode ? ' is-hidden' : ''}`}>
                <Button
                    size="small"
                    variant="contained"
                    color="error"
                    disabled={mixerElements.length === 0}
                    onClick={() => actions(MixerActions.DELETE_ALL)}
                >
                    Delete all
                </Button>
            </Box>

            {multimode ? (
                <MultiMode />
            ) : (
                <div>
                    {mixerElements.map((el) => (
                        <MixerRow
                            key={el.layerId}
                            mixerElement={el}
                            actions={actions}
                            multimode={multimode}
                            hideSlider={mixerElements.length <= 1}
                        />
                    ))}
                </div>
            )}

            <SidePanelDialog
                open={dialogOpen}
                onClose={handleCloseLayerDialog}
                side="left"
                title="Add Layer to Mixer"
                contentProps={{ sx: { p: 0 } }}
            >
                <Box sx={{ overflow: 'auto' }}>
                    <LayerTree
                        onClick={addLayerToMixer}
                        modeFilter={multimode ? undefined : mixedVectorialScalarMode}
                        showModeIndicator={true}
                        hideMixedLayer={true}
                    />
                </Box>
            </SidePanelDialog>
        </>
    );
};

export default Mixer;
